import { AbpMultiTenancyService } from 'abp-ng2-module';
import { Injectable } from '@angular/core';
import {
    ApplicationInfoDto,
    GetCurrentLoginInformationsOutput,
    SessionServiceProxy,
    TenantLoginInfoDto,
    TipoUsuarioEnum,
    UserLoginInfoDto
} from '@shared/service-proxies/service-proxies';
import { TenantChangeService } from 'account/services/tenant-change-service';
import { ProntuarioService } from '@shared/services/prontuario.service';
import { BehaviorSubject } from 'rxjs';
import * as moment from '@node_modules/moment-timezone';
import { FotoPerfilService } from '@shared/services/foto-perfil.service';

@Injectable()
export class AppSessionService {

    private _user: UserLoginInfoDto;
    private _tenant: TenantLoginInfoDto;
    private _application: ApplicationInfoDto;
    private _clinicaId: string;
    private _pacienteId: string;
    private _profissionalId: string;
    private _funcionarioId: string;
    private _bestRole: string;
    private _assinaturaClinicaValida: boolean;
    private _userName = new BehaviorSubject<string>("");
    private _espacoUsado = new BehaviorSubject<string>("0");
    private bestRoleKey = 'bestRole';

    userName$ = this._userName.asObservable();
    espacoUsado$ = this._espacoUsado.asObservable();

    constructor(
        private _sessionService: SessionServiceProxy,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private _tenantChange: TenantChangeService,
        private _prontuarioService: ProntuarioService,
        private readonly _fotoService: FotoPerfilService,
    ) {

        this._tenantChange.tenantName$.subscribe(async x => {
            await this.init(x);
        });
    }

    get application(): ApplicationInfoDto {
        return this._application;
    }

    get user(): UserLoginInfoDto {
        return this._user;
    }

    get assinaturaClinicaValida(): boolean {
        return this._assinaturaClinicaValida;
    }


    logOUt() {
        this._bestRole = null;
        this.pacienteId = null;
        this.clinicaId = null;
        this._tenant = null;
        this.profissionalId = null;
        this.funcionarioId = null;
        this._assinaturaClinicaValida = false;
        this._userName = null;
        this._espacoUsado = null;
    }

    getRoleByName(): string {
        if (!this._bestRole) {
            const stored = localStorage.getItem(this.bestRoleKey);
            if (stored) {
                this._bestRole = stored;
            } else {
                return null;
            }
        }
        if (this._bestRole == "ProfissionalSaude") {
            return "Profissional da Saúde"
        }
        return this._bestRole;
    }

    getBestRole(): string {
        if (!this._bestRole) {
            const stored = localStorage.getItem(this.bestRoleKey);
            if (stored) {
                this._bestRole = stored;
            } else {
                return null;
            }
        }
        return this._bestRole;
    }
    clearBestRole(): void {
        this._bestRole = '';
        localStorage.removeItem(this.bestRole);
    }

    set bestRole(value: string) {
        this._bestRole = value;
        localStorage.setItem(this.bestRoleKey, value);
    }

    get tipoUsuario(): string {
        switch (this.user.tipoUsuarioEnum) {
            case TipoUsuarioEnum._0:
                return "Paciente";
            case TipoUsuarioEnum._2:
                return "Profissional de Saúde";
            default:
                return "Outros";
        }
    }

    get userId(): number {
        return this.user ? this.user.id : null;
    }

    get clinicaId(): string {
        return this._clinicaId;
    }

    set clinicaId(id: string) {
        this._clinicaId = id;
    }

    get pacienteId(): string {
        return this._pacienteId;
    }

    set pacienteId(id: string) {
        this._pacienteId = id;
    }

    get funcionarioId(): string {
        return this._funcionarioId;
    }

    set funcionarioId(id: string) {
        this._funcionarioId = id;
    }

    get profissionalId(): string {
        return this._profissionalId;
    }

    set profissionalId(id: string) {
        this.profissionalId = id;
    }

    get tenant(): TenantLoginInfoDto {
        return this._tenant;
    }

    get tenantName(): string {
        return this.tenant ? this.tenant.tenancyName : null;
    }

    get primeiroNome(): string {
        return this.user.name.split(' ')[0];
    }

    get saudacao() {
        const hour = new Date().getHours();

        if (hour >= 5 && hour < 12) {
            return 'Bom dia';
        } else if (hour >= 12 && hour < 18) {
            return 'Boa tarde';
        } else {
            return 'Boa noite';
        }
    }

    getShownLoginName(): string {
        const userName = this._user.userName;
        if (!this._abpMultiTenancyService.isEnabled) {
            return userName;
        }

        return (this._tenant ? this._tenant.tenancyName : '.') + '\\' + userName;
    }

    init(tenancyName: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService.getCurrentLoginInformations(tenancyName).toPromise().then((result: GetCurrentLoginInformationsOutput) => {
                this._application = result.application;
                this._user = result.user;
                this._tenant = result.tenant;
                this._clinicaId = result.clinicaMedicaId;
                this._profissionalId = result.profisionalSaudeId;
                this._pacienteId = result.pacienteId;
                this._funcionarioId = result.funcionarioId;
                this._assinaturaClinicaValida = result.assinaturaClinicaValida
                this._fotoService.setUrlFoto(result.user?.urlFotoPerfil);
                if (result.clinicaMedicaId != null) {
                    this._prontuarioService.id = result.clinicaMedicaId;
                }
                if (result.user && result.user.espacoUtilizado) {
                    this.updateEspacoUtilizado(result.user.espacoUtilizado);
                }
                resolve(true);
            }, (err) => {
                reject(err);
            });
        });
    }

    changeTenantIfNeeded(tenantId?: number): boolean {
        if (this.isCurrentTenant(tenantId)) {
            return false;
        }

        abp.multiTenancy.setTenantIdCookie(tenantId);
        location.reload();
        return true;
    }

    private isCurrentTenant(tenantId?: number) {
        if (!tenantId && this.tenant) {
            return false;
        } else if (tenantId && (!this.tenant || this.tenant.id !== tenantId)) {
            return false;
        }

        return true;
    }

    private getInitialUserName(): string {
        return this.user.name;
    }

    setUserName(newName: string): void {
        this._userName.next(newName);
    }

    getRoles(): Array<string> {
        if (this._user?.roleNames)
            return this._user.roleNames;
        return undefined;
    }

    getRoutePrefix() {
        if (!this.user) return "/";
        const role = this.getRoles()[0].toUpperCase();
        switch (role) {
            case 'PACIENTE':
                return '/app/paciente';
                break;
            case 'FUNCIONARIO':
                return '/app/funcionario';
                break;
            case 'CLINICA':
                return '/app/clinica';
                break;
            case 'PROFISSIONALSAUDE':
                return '/app/profissional-saude';
                break;
            case 'ADMIN':
                return '/app/administrativo';
                break;
        }
    }

    userHasRole(roleName: string): boolean {
        let normalizedRoleName: string = roleName.toUpperCase();
        let normalizedRoles = this._user.roleNames.map(r => r.toUpperCase());
        if (normalizedRoleName === 'ADMIN') {
            return normalizedRoles.filter(v => !['Paciente', 'Funcionario', "Clinica", "ProfissionalSaude"].includes(v)).length > 0;
        }
        return normalizedRoles.includes(normalizedRoleName);
    }

    updateEspacoUtilizado(tamanho: string): void {
        this._espacoUsado.next(tamanho);
    }

    getEspacoUtilizado(): number {
        const tamanho = parseInt(this._espacoUsado.value, 10);
        const valor = tamanho / (1024 * 1024);
        return parseFloat(valor.toFixed(2));
    }

    assinaturaVencida(): boolean {
        if (!this.user?.assinatura?.dataEncerramento) return false;

        return moment().isAfter(this.user.assinatura.dataEncerramento);
    }

}
